import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
  PageWrap, Header, PeriodoBar, BarraBusqueda, EstadoCarga, AtencionBanner,
  ComparaBanner, MiniDonut, ModalShell, Tendencia, cs,
  SemaforoBadge as Badge, ProgresoBarra as ProgBarra,
  EstadoBadge, SemaforoResumen, ORDEN_ESTADO, SerieTemporal,
  semaforo, C, BG, calcPeriodo, prevPeriodo, todayISO, lunesISO, fmtEuro,
} from "../analisis/comun";

function pctScore(lista) {
  if (!lista || lista.length === 0) return null;
  let s = 0, m = 0;
  lista.forEach(q => { s += q.stats.verde * 2 + q.stats.amarillo; m += (q.stats.verde + q.stats.amarillo + q.stats.rojo) * 2; });
  return m === 0 ? null : Math.round((s / m) * 100);
}
const medioScore = (s) => { const a = pctScore(s.reporte_asistenta), p = pctScore(s.reporte_piso); return a !== null && p !== null ? Math.round((a + p) / 2) : (a ?? p); };

// Estado de la supervisora según evaluaciones + gastos pendientes (🔴/🟡/🟢/⬜)
const estadoSupervisora = (s) => {
  const medio = medioScore(s);
  if (medio == null && !s.reportes_completados) return 'sin_reportes';
  const st = semaforo(medio);
  if (st === 'rojo' || s.gastos.pendiente > 50) return 'critico';
  if (st === 'amarillo' || s.gastos.pendiente > 0) return 'atencion';
  return 'bien';
};

// ─── Tabs detalle ─────────────────────────────────────────────────────────────
const MetricCard = ({ label, value, color, bg, sub }) => (<div style={{ background: bg, borderRadius: 12, padding: '12px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, textAlign: 'center' }}><div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{label}</div><div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>{sub && <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>}</div>);

const CompFila = ({ label, curr, prev, invert, u = '' }) => { const delta = curr != null && prev != null ? curr - prev : null; const col = delta === null || delta === 0 ? C.gris : (invert ? delta < 0 : delta > 0) ? C.verde : C.rojo; return (<div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}><span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{label}</span><span style={{ fontSize: 12, color: '#94a3b8' }}>ant: {prev != null ? `${prev}${u}` : '—'}</span><span style={{ fontSize: 14, fontWeight: 700 }}>{curr != null ? `${curr}${u}` : '—'}</span>{delta !== null && delta !== 0 && <span style={{ fontSize: 12, fontWeight: 700, color: col, minWidth: 40 }}>{((invert ? delta < 0 : delta > 0) ? '↑' : '↓')} {Math.abs(delta)}{u}</span>}</div>); };

const TabResumen = ({ s, prev, delta }) => {
  const pctA = pctScore(s.reporte_asistenta), pctP = pctScore(s.reporte_piso);
  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
      <MetricCard label="Compras" value={s.compras.count} color={C.azul} bg={BG.azul} sub={fmtEuro(s.compras.total_gasto)} />
      <MetricCard label="Eval. Asistenta" value={pctA != null ? `${pctA}%` : '—'} color={C[semaforo(pctA)]} bg={BG[semaforo(pctA)]} sub={<Tendencia delta={delta?.asistenta} />} />
      <MetricCard label="Eval. Piso" value={pctP != null ? `${pctP}%` : '—'} color={C[semaforo(pctP)]} bg={BG[semaforo(pctP)]} sub="estado pisos" />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <MetricCard label="Ticket medio" value={fmtEuro(s.compras.avg_gasto)} color="#7c3aed" bg="#ede9fe" sub={`${s.compras.n_pisos} pisos`} />
      <MetricCard label="Gastos pend." value={fmtEuro(s.gastos.pendiente)} color={s.gastos.pendiente > 0 ? C.rojo : C.verde} bg={s.gastos.pendiente > 0 ? BG.rojo : BG.verde} sub={`Liquid.: ${fmtEuro(s.gastos.liquidado)}`} />
    </div>
    {prev && <div style={cs.sec}><h4 style={cs.sh}>📊 Comparación período anterior</h4>
      <CompFila label="Compras" curr={s.compras.count} prev={prev.compras?.count} u="" />
      <CompFila label="Gasto total" curr={Math.round(s.compras.total_gasto)} prev={Math.round(prev.compras?.total_gasto || 0)} u="€" />
      <CompFila label="Eval. Asistenta" curr={pctA} prev={pctScore(prev.reporte_asistenta)} u="%" />
      <CompFila label="Eval. Piso" curr={pctP} prev={pctScore(prev.reporte_piso)} u="%" />
      <CompFila label="Reportes completados" curr={s.reportes_completados} prev={prev.reportes_completados || 0} />
    </div>}
  </div>);
};

const TabCompras = ({ s }) => {
  const superData = [{ name: 'Mercadona', value: s.compras.super_m }, { name: 'BM / Bonpreu', value: s.compras.super_b }].filter(x => x.value > 0);
  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div style={cs.sec}><h4 style={cs.sh}>🛒 Datos de compras</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 12px' }}><div style={{ fontSize: 20, fontWeight: 800, color: C.azul }}>{s.compras.count}</div><div style={{ fontSize: 12, color: '#3b82f6' }}>pedidos entregados</div></div>
        <div style={{ background: '#f5f3ff', borderRadius: 10, padding: '10px 12px' }}><div style={{ fontSize: 20, fontWeight: 800, color: '#7c3aed' }}>{fmtEuro(s.compras.total_gasto)}</div><div style={{ fontSize: 12, color: '#7c3aed' }}>gasto total</div></div>
        <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '10px 12px' }}><div style={{ fontSize: 20, fontWeight: 800, color: C.verde }}>{fmtEuro(s.compras.avg_gasto)}</div><div style={{ fontSize: 12, color: C.verde }}>ticket medio</div></div>
        <div style={{ background: '#fef3c7', borderRadius: 10, padding: '10px 12px' }}><div style={{ fontSize: 20, fontWeight: 800, color: C.amarillo }}>{s.compras.n_pisos}</div><div style={{ fontSize: 12, color: C.amarillo }}>pisos distintos</div></div>
      </div>
    </div>
    {superData.length > 0 && <div style={cs.sec}><h4 style={cs.sh}>🏪 Reparto por supermercado <span style={{ fontWeight: 500, color: '#94a3b8', fontSize: 12 }}>(control de proveedor)</span></h4>
      {superData.map(sd => <div key={sd.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}><span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{sd.name}</span><ProgBarra pct={(sd.value / Math.max(...superData.map(x => x.value))) * 100} color={C.azul} height={8} /><span style={{ fontWeight: 700, minWidth: 30, textAlign: 'right' }}>{sd.value}</span></div>)}
    </div>}
    {s.gastos.pendiente > 0 && <div style={{ ...cs.sec, borderColor: '#fca5a5' }}><h4 style={cs.sh}>💰 Gastos pendientes de liquidar</h4>
      <div style={{ fontSize: 28, fontWeight: 800, color: C.rojo }}>{fmtEuro(s.gastos.pendiente)}</div>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Ya liquidado: {fmtEuro(s.gastos.liquidado)}</div>
    </div>}
  </div>);
};

const TabReporte = ({ s, titulo, lista }) => {
  const pct = pctScore(lista);
  const hayDatos = lista.some(q => q.stats.total > 0);
  if (!hayDatos) return (<div style={{ textAlign: 'center', padding: '40px 20px', color: C.gris }}><div style={{ fontSize: 48, marginBottom: 10 }}>📋</div><div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>Sin evaluaciones</div><div style={{ fontSize: 13, marginTop: 4 }}>No hay reportes en este período</div></div>);
  const chartData = lista.filter(q => q.stats.total > 0).map(q => ({ name: (q.emoji || '') + (q.subcategoria || ''), Bien: q.stats.verde, Regular: q.stats.amarillo, Mal: q.stats.rojo }));
  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div style={{ ...cs.sec, display: 'flex', alignItems: 'center', gap: 16 }}>
      <MiniDonut pct={pct} size={56} />
      <div><div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>{titulo}</div><Badge tipo={semaforo(pct)} label={pct != null ? `${pct}%` : '—'} /><div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{lista.reduce((s, q) => s + q.stats.total, 0)} evaluaciones · {s.reportes_completados} reportes</div></div>
    </div>
    {chartData.length > 0 && <div style={cs.sec}><h4 style={cs.sh}>📊 Por criterio</h4>
      <ResponsiveContainer width="100%" height={Math.min(360, Math.max(120, chartData.length * 46 + 20))}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 4, right: 20, top: 0, bottom: 0 }}>
          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v, n) => [`${v} veces`, n]} />
          <Bar dataKey="Bien" fill={C.verde} stackId="a" />
          <Bar dataKey="Regular" fill={C.amarillo} stackId="a" />
          <Bar dataKey="Mal" fill={C.rojo} stackId="a" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>}
    <div style={cs.sec}><h4 style={cs.sh}>📋 Detalle</h4>
      {lista.filter(q => q.stats.total > 0).map(q => { const t = q.stats.verde + q.stats.amarillo + q.stats.rojo; if (t === 0) return null; const pV = Math.round((q.stats.verde / t) * 100); const pA = Math.round((q.stats.amarillo / t) * 100); const pR = 100 - pV - pA; return (<div key={q.id_pregunta} style={{ marginBottom: 12 }}><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><span style={{ fontSize: 18 }}>{q.emoji}</span><span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{q.subcategoria}</span><span style={{ fontSize: 13, fontWeight: 800, color: C[semaforo(pV)] }}>{pV}% 🟢</span></div><div style={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden' }}>{pV > 0 && <div style={{ width: `${pV}%`, background: C.verde }} />}{pA > 0 && <div style={{ width: `${pA}%`, background: C.amarillo }} />}{pR > 0 && <div style={{ width: `${pR}%`, background: C.rojo }} />}</div><div style={{ display: 'flex', gap: 12, marginTop: 3 }}><span style={{ fontSize: 11, color: C.verde }}>🟢{q.stats.verde}</span><span style={{ fontSize: 11, color: C.amarillo }}>🟡{q.stats.amarillo}</span><span style={{ fontSize: 11, color: C.rojo }}>🔴{q.stats.rojo}</span></div></div>); })}
    </div>
  </div>);
};

const TabEvolucion = ({ s }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
    <SerieTemporal
      data={s.serie_eval || []}
      series={[
        { key: 'asistenta', label: 'Asistenta %', color: '#22c55e' },
        { key: 'piso', label: 'Piso %', color: '#3b82f6' },
      ]}
      titulo="Evaluación por semana"
      height={190}
    />
    <SerieTemporal
      data={s.serie_compras || []}
      series={[{ key: 'compras', label: 'Compras', color: '#8b5cf6' }]}
      titulo="Compras por semana"
      height={170}
    />
  </div>
);

const DetalleModal = ({ s, prev, delta, onClose }) => {
  const [tab, setTab] = useState('resumen');
  const st = semaforo(medioScore(s));
  return (
    <ModalShell
      titulo={`${s.nombre} ${s.apellidos}`}
      subtitulo={`${s.user} · ${s.n_pisos_gestionados} pisos · ${s.reportes_completados} reportes`}
      colorBorde={C[st]}
      tabs={[{ key: 'resumen', label: '📈 Resumen' }, { key: 'compras', label: '🛒 Compras' }, { key: 'asistenta', label: '🧹 Asistenta' }, { key: 'piso', label: '🏠 Piso' }, { key: 'evolucion', label: '📊 Evolución' }]}
      tab={tab} setTab={setTab} onClose={onClose}
    >
      {tab === 'resumen' && <TabResumen s={s} prev={prev} delta={delta} />}
      {tab === 'compras' && <TabCompras s={s} />}
      {tab === 'asistenta' && <TabReporte s={s} titulo="Evaluación de Asistentas" lista={s.reporte_asistenta || []} />}
      {tab === 'piso' && <TabReporte s={s} titulo="Estado de Pisos" lista={s.reporte_piso || []} />}
      {tab === 'evolucion' && <TabEvolucion s={s} />}
    </ModalShell>
  );
};

// ─── Tarjeta supervisora ──────────────────────────────────────────────────────
const TarjetaSupervisora = ({ s, delta, onClick }) => {
  const pctA = pctScore(s.reporte_asistenta), pctP = pctScore(s.reporte_piso);
  const medio = medioScore(s);
  const st = semaforo(medio);
  const alert = s.gastos.pendiente > 50 || st === 'rojo';
  return (<div onClick={onClick} style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', marginBottom: 8, cursor: 'pointer', border: `1.5px solid ${alert ? '#fca5a5' : '#e2e8f0'}`, borderLeftWidth: 5, borderLeftColor: C[st], boxShadow: alert ? '0 2px 8px rgba(220,38,38,0.1)' : '0 1px 4px rgba(0,0,0,0.05)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <MiniDonut pct={medio} size={50} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
          {alert && <span>⚠️</span>}
          <span style={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>{s.nombre} {s.apellidos}</span>
          <EstadoBadge estado={estadoSupervisora(s)} />
          <Tendencia delta={delta?.asistenta} />
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{s.user} · {s.n_pisos_gestionados} pisos</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {s.gastos.pendiente > 50 && <span style={{ fontSize: 12, fontWeight: 700, color: C.rojo, background: BG.rojo, borderRadius: 12, padding: '3px 8px' }}>💰 {fmtEuro(s.gastos.pendiente)} pend.</span>}
          <span style={{ fontSize: 12, fontWeight: 700, color: C.azul, background: BG.azul, borderRadius: 12, padding: '3px 8px' }}>🛒 {s.compras.count} · {fmtEuro(s.compras.total_gasto)}</span>
          {pctA !== null && <Badge tipo={semaforo(pctA)} label={`🧹 ${pctA}%`} />}
          {pctP !== null && <Badge tipo={semaforo(pctP)} label={`🏠 ${pctP}%`} />}
          {s.gastos.pendiente > 0 && s.gastos.pendiente <= 50 && <span style={{ fontSize: 12, fontWeight: 700, color: C.amarillo, background: BG.amarillo, borderRadius: 12, padding: '3px 8px' }}>💰 {fmtEuro(s.gastos.pendiente)}</span>}
        </div>
      </div>
      <span style={{ color: '#94a3b8', fontSize: 18, flexShrink: 0 }}>▶</span>
    </div>
  </div>);
};

// ─── Componente principal ─────────────────────────────────────────────────────
export default function AnalisisSupervisoras() {
  const navigate = useNavigate();
  const [periodoKey, setPeriodoKey] = useState('mes');
  const [custom, setCustom] = useState({ desde: lunesISO(), hasta: todayISO() });
  const [datos, setDatos] = useState(null);
  const [datosPrev, setDatosPrev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('estado');
  const [filtroEstado, setFiltroEstado] = useState(null);

  const periodo = periodoKey === 'custom' ? custom : calcPeriodo(periodoKey);
  const { desde, hasta } = periodo;

  const fetchP = useCallback(async (d, h) => (await axios.get(`/api/analisis/supervisoras?desde=${d}&hasta=${h}`)).data, []);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try { const pp = prevPeriodo({ desde, hasta }); const [curr, prev] = await Promise.all([fetchP(desde, hasta), fetchP(pp.desde, pp.hasta)]); setDatos(curr); setDatosPrev(prev); }
    catch (e) { setError(e.response?.data?.error || 'Error al cargar.'); }
    finally { setLoading(false); }
  }, [desde, hasta, fetchP]);

  useEffect(() => { cargar(); }, [cargar]);

  const prevMap = useMemo(() => Object.fromEntries((datosPrev?.supervisoras || []).map(s => [s.user, s])), [datosPrev]);
  const deltaMap = useMemo(() => { const o = {}; (datos?.supervisoras || []).forEach(s => { const p = prevMap[s.user]; o[s.user] = { asistenta: pctScore(s.reporte_asistenta) !== null && p ? pctScore(s.reporte_asistenta) - pctScore(p.reporte_asistenta) : null }; }); return o; }, [datos, prevMap]);

  const lista = useMemo(() => {
    let l = datos?.supervisoras || [];
    if (busqueda.trim()) { const q = busqueda.toLowerCase(); l = l.filter(s => (s.nombre + ' ' + s.apellidos + ' ' + s.user).toLowerCase().includes(q)); }
    if (filtroEstado) l = l.filter(s => estadoSupervisora(s) === filtroEstado);
    const s = [...l];
    if (orden === 'estado') s.sort((a, b) => ORDEN_ESTADO[estadoSupervisora(a)] - ORDEN_ESTADO[estadoSupervisora(b)]);
    else if (orden === 'compras') s.sort((a, b) => b.compras.count - a.compras.count);
    else if (orden === 'gasto') s.sort((a, b) => b.compras.total_gasto - a.compras.total_gasto);
    else if (orden === 'eval') s.sort((a, b) => (medioScore(a) ?? 101) - (medioScore(b) ?? 101));
    else if (orden === 'pend') s.sort((a, b) => b.gastos.pendiente - a.gastos.pendiente);
    else if (orden === 'nombre') s.sort((a, b) => a.nombre.localeCompare(b.nombre));
    return s;
  }, [datos, busqueda, orden, filtroEstado]);

  const selObj = useMemo(() => lista.find(s => s.user === sel), [lista, sel]);

  // Resumen / KPIs / atención
  const todas = datos?.supervisoras || [];
  const counts = { verde: 0, amarillo: 0, rojo: 0, gris: 0 };
  todas.forEach(s => { counts[semaforo(medioScore(s))]++; });
  const totalGasto = todas.reduce((sum, s) => sum + s.compras.total_gasto, 0);
  const totalComp = todas.reduce((sum, s) => sum + s.compras.count, 0);
  const totalPend = todas.reduce((sum, s) => sum + s.gastos.pendiente, 0);
  const nPend = todas.filter(s => s.gastos.pendiente > 50).length;
  const estadoCounts = useMemo(() => {
    const c = { critico: 0, atencion: 0, bien: 0, sin_reportes: 0 };
    todas.forEach(s => { c[estadoSupervisora(s)]++; });
    return c;
  }, [todas]);
  const pieData = [{ name: 'Verde', value: counts.verde, fill: C.verde }, { name: 'Amarillo', value: counts.amarillo, fill: C.amarillo }, { name: 'Rojo', value: counts.rojo, fill: C.rojo }].filter(x => x.value > 0);

  const atencion = useMemo(() => {
    const items = [];
    if (counts.rojo > 0) items.push({ icon: '🔴', severidad: 'rojo', n: counts.rojo, texto: `${counts.rojo} supervisora${counts.rojo !== 1 ? 's' : ''} con evaluación baja` });
    if (nPend > 0) items.push({ icon: '💰', severidad: 'amarillo', n: nPend, texto: `${nPend} supervisora${nPend !== 1 ? 's' : ''} con gastos pendientes (${fmtEuro(totalPend)} total)` });
    return items;
  }, [counts.rojo, nPend, totalPend]);

  const pp = prevPeriodo({ desde, hasta });
  const vacio = !loading && !error && lista.length === 0;

  return (
    <PageWrap>
      <Header icon="👩‍💼" title="Mis Supervisoras" count={lista.length} countLabel="supervisoras" onBack={() => navigate(-1)} />
      <PeriodoBar periodoKey={periodoKey} setPeriodoKey={setPeriodoKey} custom={custom} setCustom={setCustom} />
      {!loading && !error && <ComparaBanner prev={pp} />}

      <EstadoCarga loading={loading} error={error} onRetry={cargar} />

      {!loading && !error && datos && (
        <>
          <AtencionBanner items={atencion} okTexto="Todas las supervisoras al día, sin alertas." />

          <SemaforoResumen counts={estadoCounts} filtro={filtroEstado} setFiltro={setFiltroEstado} />

          {/* Resumen visual */}
          <div style={{ background: '#fff', margin: '8px 14px 4px', borderRadius: 16, padding: '14px 16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {pieData.length > 0 && <PieChart width={100} height={100}><Pie data={pieData} cx={45} cy={45} innerRadius={26} outerRadius={45} dataKey="value" paddingAngle={3}>{pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><Tooltip formatter={(v, n) => [`${v} supervisoras`, n]} /></PieChart>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, flex: 1, minWidth: 180 }}>
              <div style={{ background: BG.azul, borderRadius: 10, padding: '8px 10px' }}><div style={{ fontSize: 18, fontWeight: 800, color: C.azul }}>{totalComp}</div><div style={{ fontSize: 11, color: C.azul }}>🛒 compras totales</div></div>
              <div style={{ background: '#f5f3ff', borderRadius: 10, padding: '8px 10px' }}><div style={{ fontSize: 16, fontWeight: 800, color: '#7c3aed' }}>{fmtEuro(totalGasto)}</div><div style={{ fontSize: 11, color: '#7c3aed' }}>💶 gasto total</div></div>
              <div style={{ background: BG.verde, borderRadius: 10, padding: '8px 10px' }}><div style={{ fontSize: 18, fontWeight: 800, color: C.verde }}>{counts.verde}</div><div style={{ fontSize: 11, color: C.verde }}>🟢 OK</div></div>
              <div style={{ background: BG.rojo, borderRadius: 10, padding: '8px 10px' }}><div style={{ fontSize: 18, fontWeight: 800, color: C.rojo }}>{counts.rojo}</div><div style={{ fontSize: 11, color: C.rojo }}>🔴 Críticas</div></div>
            </div>
          </div>

          <BarraBusqueda
            busqueda={busqueda} setBusqueda={setBusqueda}
            orden={orden} setOrden={setOrden}
            placeholder="🔍 Buscar supervisora…"
            ordenes={[
              { key: 'estado', label: '🚦 Estado' },
              { key: 'compras', label: 'Más compras' },
              { key: 'gasto', label: 'Mayor gasto' },
              { key: 'eval', label: 'Peor evaluación' },
              { key: 'pend', label: 'Más pendiente €' },
              { key: 'nombre', label: 'Nombre A–Z' },
            ]}
          />

          <div style={{ padding: '0 14px' }}>
            <EstadoCarga vacio={vacio} />
            {lista.map(s => <TarjetaSupervisora key={s.user} s={s} delta={deltaMap[s.user]} onClick={() => setSel(s.user)} />)}
          </div>
        </>
      )}

      {sel && selObj && <DetalleModal s={selObj} prev={prevMap[sel]} delta={deltaMap[sel]} onClose={() => setSel(null)} />}
    </PageWrap>
  );
}
